/**
 * Storage Utility
 * Handles auth tokens scoped by machine name
 * Uses localStorage to keep independent tokens per machine
 */

const TOKEN_KEY_PREFIX = 'auth_token_';
const TOKEN_EXPIRY_KEY_PREFIX = 'auth_token_expiry_';
const FETCH_TOKEN_KEY_PREFIX = 'fetch_token_';

function normalizeMachineKey(machineName = 'default') {
    const normalized = String(machineName || 'default')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');

    if (!normalized || normalized === 'self') {
        return 'default';
    }

    return normalized;
}

function getTokenKey(machineName = 'default') {
    return `${TOKEN_KEY_PREFIX}${normalizeMachineKey(machineName)}`;
}

function getExpiryKey(machineName = 'default') {
    return `${TOKEN_EXPIRY_KEY_PREFIX}${normalizeMachineKey(machineName)}`;
}

function getFetchTokenKey(machineName = 'default') {
    return `${FETCH_TOKEN_KEY_PREFIX}${normalizeMachineKey(machineName)}`;
}

/**
 * Store auth token with expiry
 * @param {string} token - JWT token
 * @param {number} expiryHours - Hours until expiry (default 168)
 */
export function saveToken(token, machineName = 'default', expiryHours = 168) {
    const expiry = Date.now() + (expiryHours * 60 * 60 * 1000);
    localStorage.setItem(getTokenKey(machineName), token);
    localStorage.setItem(getExpiryKey(machineName), expiry.toString());
}

/**
 * Get auth token if valid
 * @returns {string|null} Token or null if expired/missing
 */
export function getToken(machineName = 'default') {
    const token = localStorage.getItem(getTokenKey(machineName));
    const expiry = localStorage.getItem(getExpiryKey(machineName));
    
    if (!token || !expiry) {
        return null;
    }
    
    if (Date.now() > parseInt(expiry)) {
        clearToken(machineName);
        return null;
    }
    
    return token;
}

/**
 * Clear auth token
 */
export function clearToken(machineName = 'default') {
    localStorage.removeItem(getTokenKey(machineName));
    localStorage.removeItem(getExpiryKey(machineName));
    clearFetchCredentials(machineName);
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated(machineName = 'default') {
    return getToken(machineName) !== null;
}

/**
 * Clear all machine auth tokens
 */
export function clearAllTokens() {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
        if (
            key.startsWith(TOKEN_KEY_PREFIX)
            || key.startsWith(TOKEN_EXPIRY_KEY_PREFIX)
            || key.startsWith(FETCH_TOKEN_KEY_PREFIX)
            || key.startsWith('fetch_key_')
            || key.startsWith('fetch_expiry_')
        ) {
            localStorage.removeItem(key);
        }
    });
}

/**
 * Store fetch token credentials with expiry.
 * @param {string} fetchToken
 * @param {string} machineName
 * @param {number} expiryHours
 */
export function saveFetchCredentials(fetchToken, machineName = 'default', expiryHours = 168) {
    if (!fetchToken) {
        clearFetchCredentials(machineName);
        return;
    }

    localStorage.setItem(getFetchTokenKey(machineName), fetchToken);
}

/**
 * Retrieve fetch credentials if still valid.
 * @param {string} machineName
 * @returns {{fetchToken: string} | null}
 */
export function getFetchCredentials(machineName = 'default') {
    const fetchToken = localStorage.getItem(getFetchTokenKey(machineName));

    if (!fetchToken) {
        return null;
    }

    return { fetchToken };
}

/**
 * Clear fetch token credentials.
 * @param {string} machineName
 */
export function clearFetchCredentials(machineName = 'default') {
    localStorage.removeItem(getFetchTokenKey(machineName));
    localStorage.removeItem(`fetch_key_${normalizeMachineKey(machineName)}`);
    localStorage.removeItem(`fetch_expiry_${normalizeMachineKey(machineName)}`);
}
